import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { UserService, User, UpdateMePayload, ChangePasswordPayload } from '../../core/services/user';
import { AuthService } from '../../core/services/auth';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  templateUrl: './settings.html',
  styleUrl: './settings.css'
})
export class SettingsComponent implements OnInit {
  private userService = inject(UserService);
  private authService = inject(AuthService);
  private fb = inject(FormBuilder);

  // ─── Tab Navigation ──────────────────────────────────────────────────────────
  activeTab = signal<'distribution' | 'profile' | 'security' | 'cdn' | 'compliance' | 'api-keys'>('distribution');

  // ─── Distribution Settings (existing) ─────────────────────────────────────
  regions = [
    { id: 'na',   name: 'North America (NA)',          active: true,  language: 'en-US', timezone: 'UTC-5' },
    { id: 'emea', name: 'Europe, Mid-East, Africa (EMEA)', active: true, language: 'en-GB', timezone: 'UTC+1' },
    { id: 'apac', name: 'Asia-Pacific (APAC)',           active: false, language: 'en-SG', timezone: 'UTC+8' }
  ];
  availableLanguages = [
    { code: 'en', name: 'English',  selected: true  },
    { code: 'tr', name: 'Turkish',  selected: true  },
    { code: 'de', name: 'Deutsch',  selected: false },
    { code: 'es', name: 'Español',  selected: false },
    { code: 'fr', name: 'Français', selected: false },
    { code: 'ar', name: 'العربية', selected: false  },
    { code: 'da', name: 'Danish', selected: false },
    { code: 'sv', name: 'Swedish', selected: false },
    { code: 'no', name: 'Norwegian', selected: false },
    { code: 'fi', name: 'Finnish', selected: false },
  ];
  toggleRegion(region: any)   { region.active = !region.active; }
  toggleLanguage(lang: any)   { lang.selected = !lang.selected; }

  // ─── Profile ─────────────────────────────────────────────────────────────────
  currentUser = signal<User | null>(null);
  isLoadingProfile = signal(false);
  profileSaving = signal(false);
  profileError = signal<string | null>(null);
  profileSuccess = signal<string | null>(null);
  photoUploading = signal(false);

  profileForm = this.fb.group({
    first_name: ['', Validators.required],
    last_name:  ['', Validators.required],
    email:      ['', [Validators.required, Validators.email]],
  });

  // ─── Security ────────────────────────────────────────────────────────────────
  passwordSaving = signal(false);
  passwordError = signal<string | null>(null);
  passwordSuccess = signal<string | null>(null);

  passwordForm = this.fb.group({
    current_password: ['', Validators.required],
    new_password:     ['', [Validators.required, Validators.minLength(6)]],
    confirm_password: ['', Validators.required],
  });

  // ─── Lifecycle ───────────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.loadProfile();
  }

  // ─── Profile Methods ─────────────────────────────────────────────────────────
  loadProfile(): void {
    this.isLoadingProfile.set(true);
    this.userService.getMe().subscribe({
      next: (user) => {
        this.currentUser.set(user);
        this.profileForm.patchValue({
          first_name: user.first_name,
          last_name:  user.last_name,
          email:      user.email,
        });
        // Also update the global auth signal
        this.authService.currentUser.set(user);
        this.isLoadingProfile.set(false);
      },
      error: () => this.isLoadingProfile.set(false)
    });
  }

  saveProfile(): void {
    if (this.profileForm.invalid) { this.profileForm.markAllAsTouched(); return; }
    this.profileSaving.set(true);
    this.profileError.set(null);
    this.profileSuccess.set(null);
    const v = this.profileForm.getRawValue();
    const payload: UpdateMePayload = {
      first_name: v.first_name ?? undefined,
      last_name:  v.last_name ?? undefined,
      email:      v.email ?? undefined,
    };
    this.userService.updateMe(payload).subscribe({
      next: (user) => {
        this.currentUser.set(user);
        this.authService.currentUser.set(user);
        this.profileSaving.set(false);
        this.profileSuccess.set('Profile updated successfully!');
        setTimeout(() => this.profileSuccess.set(null), 3000);
      },
      error: (err) => {
        this.profileSaving.set(false);
        this.profileError.set(err?.error?.detail ?? 'Could not save profile.');
      }
    });
  }

  onPhotoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    const maxBytes = 2 * 1024 * 1024; // 2 MB
    if (file.size > maxBytes) {
      this.profileError.set('Photo must be 2 MB or smaller.');
      input.value = '';
      return;
    }

    this.profileError.set(null);
    this.photoUploading.set(true);
    this.userService.uploadPhoto(file).subscribe({
      next: (user) => {
        this.currentUser.set(user);
        this.authService.currentUser.set(user);
        this.photoUploading.set(false);
      },
      error: () => this.photoUploading.set(false)
    });
    input.value = '';
  }

  getInitials(): string {
    const u = this.currentUser();
    return u ? `${u.first_name?.[0] ?? ''}${u.last_name?.[0] ?? ''}`.toUpperCase() : '';
  }

  // ─── Password Methods ─────────────────────────────────────────────────────────
  savePassword(): void {
    if (this.passwordForm.invalid) { this.passwordForm.markAllAsTouched(); return; }
    const v = this.passwordForm.value;
    if (v.new_password !== v.confirm_password) {
      this.passwordError.set('New passwords do not match.');
      return;
    }
    this.passwordSaving.set(true);
    this.passwordError.set(null);
    this.passwordSuccess.set(null);
    const payload: ChangePasswordPayload = {
      current_password: v.current_password!,
      new_password:     v.new_password!,
    };
    this.userService.changePassword(payload).subscribe({
      next: () => {
        this.passwordSaving.set(false);
        this.passwordSuccess.set('Password changed successfully!');
        this.passwordForm.reset();
        setTimeout(() => this.passwordSuccess.set(null), 3000);
      },
      error: (err) => {
        this.passwordSaving.set(false);
        this.passwordError.set(err?.error?.detail ?? 'Could not change password.');
      }
    });
  }
}
